import type { ReactNode } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import { MdOutlineEditNote } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { User } from "../../types/Types";
import {
  getConnectionPermission,
  type ConnectionTarget,
} from "../../utils/connectionUserUtils";
import { ExplorerConnectionLimitLockedButton } from "../Connections/ExplorerConnectionLimitLockedButton";
import {
  ACTION_BTN_BASE,
  ACTION_BTN_CLASS,
  ACTIONS_COL_WIDTH,
  BTN_GAP,
  BTN_HEIGHT,
  BTN_SLOT,
  CANCEL_BTN_STYLE,
  COL,
  CONNECT_BTN_STYLE,
  PROFILE_BTN_STYLE,
  UPGRADE_BTN_STYLE,
  stopCardSelect,
  type ConnectionState,
} from "./eventAttendeeCardShared";

export interface EventAttendeeCardActionsProps {
  user: User;
  actionUser: User;
  connectionState: ConnectionState;
  isCurrentUser: boolean;
  currentUserMembership: ConnectionTarget | string | undefined;
  explorerLimitReached?: boolean;
  isExplorerUser?: boolean;
  onBlockedConnectionAction?: () => void;
  onViewProfile: (user: User) => void;
  onSaveNote?: (user: User) => void;
  onMessage: (id: number) => void;
  onConnect: (user: User) => void;
  onCancelRequest: (id: number) => void;
  onAccept: (id: number, user: User) => void;
  onIgnore: (id: number) => void;
  sendConnectionId: number | null;
  cancellingId: number | null;
  hasNote?: boolean;
  mobile?: boolean;
}

function EventAttendeeCardActions({
  user,
  actionUser,
  connectionState,
  isCurrentUser,
  currentUserMembership,
  explorerLimitReached = false,
  isExplorerUser = false,
  onBlockedConnectionAction,
  onViewProfile,
  onSaveNote,
  onMessage,
  onConnect,
  onCancelRequest,
  onAccept,
  onIgnore,
  sendConnectionId,
  cancellingId,
  hasNote = false,
  mobile = false,
}: EventAttendeeCardActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { canConnect, upgradeKind } = getConnectionPermission(
    currentUserMembership,
    {
      membershipName: user.membershipName || actionUser.membershipName,
      explorerAttendeeType:
        user.explorerAttendeeType ?? actionUser.explorerAttendeeType,
    },
    isExplorerUser
  );
  const shouldShowConnectFallback = upgradeKind === "none";

  const profileButton = () => (
    <Button
      variant="light"
      className={`${ACTION_BTN_CLASS} fw-medium text-dark`}
      style={PROFILE_BTN_STYLE}
      onClick={(e) => {
        stopCardSelect(e);
        onViewProfile(user);
      }}
    >
      {t("connections.viewProfile")}
    </Button>
  );

  const noteButton = () =>
    onSaveNote ? (
      <Button
        variant="outline-secondary"
        className="d-flex align-items-center justify-content-center gap-1 rounded-2 shadow-sm text-nowrap"
        style={{
          ...ACTION_BTN_BASE,
          ...(mobile
            ? {
                width: "100%",
                minWidth: 0,
                maxWidth: "none",
                padding: "0 12px",
              }
            : {
                flexShrink: 0,
                minWidth: COL.noteBtn,
                width: COL.noteBtn,
                maxWidth: COL.noteBtn,
                padding: "0 10px",
              }),
          background: hasNote ? "#e8f4f8" : "#fff",
          borderColor: hasNote ? "#075CCB" : "#dee2e6",
          color: hasNote ? "#075CCB" : "#6c757d",
        }}
        title={hasNote ? t("attendeeNotes.editNote") : t("attendeeNotes.addNote")}
        aria-label={
          hasNote ? t("attendeeNotes.editNote") : t("attendeeNotes.addNote")
        }
        onClick={(e) => {
          stopCardSelect(e);
          onSaveNote(user);
        }}
      >
        <MdOutlineEditNote size={18} aria-hidden className="flex-shrink-0" />
        <span>
          {hasNote ? t("attendeeNotes.editNote") : t("attendeeNotes.addNote")}
        </span>
      </Button>
    ) : null;

  const buildSecondary = (): ReactNode => {
    if (isCurrentUser) return null;

    if (connectionState === "connected") {
      return (
        <Button
          variant="primary"
          className={`${ACTION_BTN_CLASS} fw-medium text-white`}
          style={CONNECT_BTN_STYLE}
          onClick={(e) => {
            stopCardSelect(e);
            onMessage(actionUser.id);
          }}
        >
          {t("connections.message")}
        </Button>
      );
    }

    if (connectionState === "sent") {
      return (
        <Button
          variant="outline-danger"
          className="w-100 d-flex align-items-center justify-content-center rounded-2 text-nowrap fw-medium shadow-sm attendee-cancel-request-btn"
          style={CANCEL_BTN_STYLE}
          onClick={(e) => {
            stopCardSelect(e);
            onCancelRequest(actionUser.id);
          }}
          disabled={cancellingId === actionUser.id}
        >
          {cancellingId === actionUser.id
            ? t("connections.cancelling")
            : t("connections.cancelRequest")}
        </Button>
      );
    }

    if (connectionState === "received") {
      return (
        <div className="d-flex w-100" style={{ gap: BTN_GAP }}>
          {explorerLimitReached ? (
            <ExplorerConnectionLimitLockedButton
              action="accept"
              className="flex-fill rounded-2 shadow-sm"
              style={CONNECT_BTN_STYLE}
              onLimitClick={() => {
                onBlockedConnectionAction?.();
              }}
            />
          ) : (
            <Button
              variant="primary"
              className={`flex-fill ${ACTION_BTN_CLASS} fw-medium text-white`}
              style={CONNECT_BTN_STYLE}
              onClick={(e) => {
                stopCardSelect(e);
                onAccept(actionUser.id, actionUser);
              }}
            >
              {t("connections.accept")}
            </Button>
          )}
          <Button
            variant="outline-secondary"
            className={`flex-fill ${ACTION_BTN_CLASS} fw-medium`}
            style={ACTION_BTN_BASE}
            onClick={(e) => {
              stopCardSelect(e);
              onIgnore(actionUser.id);
            }}
          >
            {isExplorerUser ? t("connections.decline") : t("connections.ignore")}
          </Button>
        </div>
      );
    }

    if (canConnect || shouldShowConnectFallback) {
      return explorerLimitReached ? (
        <ExplorerConnectionLimitLockedButton
          action="connect"
          className="w-100 rounded-2 shadow-sm"
          style={CONNECT_BTN_STYLE}
          onLimitClick={() => {
            onBlockedConnectionAction?.();
          }}
        />
      ) : (
        <Button
          variant="primary"
          className={`${ACTION_BTN_CLASS} fw-medium text-white`}
          style={CONNECT_BTN_STYLE}
          disabled={sendConnectionId === actionUser.id}
          onClick={(e) => {
            stopCardSelect(e);
            onConnect(actionUser);
          }}
        >
          {sendConnectionId === actionUser.id
            ? t("connections.sending")
            : t("connections.connect")}
        </Button>
      );
    }

    if (upgradeKind !== "executive" && upgradeKind !== "business") {
      return null;
    }

    const upgradeLabel =
      upgradeKind === "executive"
        ? t("connections.upgradeToExecutiveMembership")
        : t("connections.upgradeToBusinessMembership");
    const upgradePlan =
      upgradeKind === "executive"
        ? "Executive Membership"
        : "Business Membership";

    return (
      <button
        type="button"
        className="w-100 d-inline-flex align-items-center justify-content-start text-secondary rounded-2 min-w-0 overflow-hidden shadow-sm"
        style={UPGRADE_BTN_STYLE}
        title={upgradeLabel}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/renew-membership?plan=${encodeURIComponent(upgradePlan)}`);
        }}
      >
        <FaLock size={12} className="flex-shrink-0 me-2 opacity-75" aria-hidden />
        <span className="text-truncate">{upgradeLabel}</span>
      </button>
    );
  };

  const note = noteButton();
  const secondary = buildSecondary();
  // Keep three action slots on desktop so "You" rows align with other attendees / header.
  const actionsWidth = ACTIONS_COL_WIDTH;

  if (mobile) {
    return (
      <div
        className="d-flex flex-column w-100"
        style={{ gap: BTN_GAP }}
        onClick={stopCardSelect}
      >
        {note ? <div className="w-100">{note}</div> : null}
        <div className="d-flex align-items-center w-100" style={{ gap: BTN_GAP }}>
          <div className="min-w-0" style={{ flex: "1 1 0%" }}>
            {profileButton()}
          </div>
          {!isCurrentUser ? (
            <div className="min-w-0" style={{ flex: "1 1 0%" }}>
              {secondary ?? (
                <span
                  className="d-block w-100"
                  style={{ height: BTN_HEIGHT }}
                  aria-hidden
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center flex-nowrap flex-shrink-0"
      style={{
        gap: BTN_GAP,
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: actionsWidth,
      }}
      onClick={stopCardSelect}
    >
      {note ?? <div style={BTN_SLOT} aria-hidden />}
      <div style={BTN_SLOT}>{profileButton()}</div>
      <div style={BTN_SLOT}>
        {secondary ?? (
          <span
            className="d-block w-100"
            style={{ height: BTN_HEIGHT }}
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}

export default EventAttendeeCardActions;
